import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useUpdateWorker, type RateSchedule, type TMWorker } from "@/hooks/usePersonnelProjects";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  worker: TMWorker | null;
  rates: RateSchedule[];
  projectId: string;
}

export function EditWorkerDialog({ open, onOpenChange, worker, rates, projectId }: Props) {
  const updateWorker = useUpdateWorker();
  const [workerName, setWorkerName] = useState("");
  const [rateScheduleId, setRateScheduleId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [phone, setPhone] = useState("");
  const [tradeClassification, setTradeClassification] = useState("");
  const [billRateOverride, setBillRateOverride] = useState("");
  const [costRateOverride, setCostRateOverride] = useState("");

  useEffect(() => {
    if (!open || !worker) return;
    setWorkerName(worker.worker_name ?? "");
    setRateScheduleId(worker.rate_schedule_id ?? "");
    setStartDate(worker.start_date ?? "");
    setPhone(worker.phone ?? "");
    setTradeClassification(worker.trade_classification ?? "");
    setBillRateOverride(worker.bill_rate_override != null ? String(worker.bill_rate_override) : "");
    setCostRateOverride(worker.cost_rate_override != null ? String(worker.cost_rate_override) : "");
  }, [open, worker]);

  const selectedRate = rates.find((r) => r.id === rateScheduleId);

  const handleSave = async () => {
    if (!worker) return;
    if (!workerName.trim()) return toast.error("Worker name is required");
    if (!rateScheduleId) return toast.error("Role is required");

    try {
      await updateWorker.mutateAsync({
        workerId: worker.id,
        projectId,
        updates: {
          worker_name: workerName.trim(),
          rate_schedule_id: rateScheduleId,
          start_date: startDate || null,
          phone: phone.trim() || null,
          trade_classification: tradeClassification.trim() || null,
          bill_rate_override: billRateOverride ? parseFloat(billRateOverride) : null,
          cost_rate_override: costRateOverride ? parseFloat(costRateOverride) : null,
        },
      });
      toast.success("Worker updated");
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message ?? "Could not update worker");
    }
  };

  const isPortalLinked = !!worker?.portal_user_id || !!(worker?.portal_status && worker.portal_status !== "not_invited");
  const isPending = updateWorker.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit Worker
          </DialogTitle>
          <DialogDescription>
            Update this worker’s project details directly from the Workers tab.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-worker-name">Worker name</Label>
            <Input
              id="edit-worker-name"
              value={workerName}
              onChange={(e) => setWorkerName(e.target.value)}
              placeholder="Worker name"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select value={rateScheduleId} onValueChange={setRateScheduleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {rates.map((rate) => (
                    <SelectItem key={rate.id} value={rate.id}>
                      {rate.role_name}{!rate.is_active ? " (inactive)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-worker-start-date">Start date</Label>
              <Input
                id="edit-worker-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="edit-worker-phone">Phone</Label>
              <Input
                id="edit-worker-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-worker-trade">Trade classification</Label>
              <Input
                id="edit-worker-trade"
                value={tradeClassification}
                onChange={(e) => setTradeClassification(e.target.value)}
                placeholder="Trade / classification"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="edit-worker-bill-rate">Bill rate override</Label>
              <Input
                id="edit-worker-bill-rate"
                type="number"
                step="0.01"
                min="0"
                value={billRateOverride}
                onChange={(e) => setBillRateOverride(e.target.value)}
                placeholder={selectedRate ? `Role default: $${selectedRate.bill_rate_regular}` : "Role default"}
              />
              <p className="text-[11px] text-muted-foreground">Leave blank to use role default</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="edit-worker-cost-rate">Cost rate override</Label>
              <Input
                id="edit-worker-cost-rate"
                type="number"
                step="0.01"
                min="0"
                value={costRateOverride}
                onChange={(e) => setCostRateOverride(e.target.value)}
                placeholder={selectedRate ? `Role default: $${selectedRate.cost_rate}` : "Role default"}
              />
              <p className="text-[11px] text-muted-foreground">Leave blank to use role default</p>
            </div>
          </div>

          {worker?.portal_email && (
            <div className="grid gap-2">
              <Label htmlFor="edit-worker-portal-email">Portal email</Label>
              <Input id="edit-worker-portal-email" value={worker.portal_email} disabled />
            </div>
          )}

          {isPortalLinked && (
            <div className="flex items-center gap-2 rounded-md border p-3 text-sm text-muted-foreground">
              <Badge variant="outline">Portal-linked</Badge>
              <span>Signed workers can be edited here without changing their account linkage.</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isPending || !workerName.trim() || !rateScheduleId}>
            {isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
